import React, { useEffect, useRef, useState } from "react";
import Button from "@mui/material/Button";
import StandardModal from "../../../components/modales/StandardModal";
import axios from "axios";
import { EmptyState, type DataTableColumn } from "@routes-plan/ui";
import { useT } from "../../../i18n";
import { ModalTable } from "../components/ReportLayout";
import { formatCurrency as companyMoney } from '../../../utils/format/currency';

type DefaultClientItem = {
  id: number;
  loanId: number;
  clientIdNumber: string | number;
  clientFullName: string;
  loanDate: string;
  loanValue: number;
  balance: number;
  paymentTotal: number;
  defaultDays: number;
};

interface DefaultClientsModalProps {
  open: boolean;
  onClose: () => void;
  routeId: number | null;
  routeName: string | null;
  date: string | null;
  showAlert: (msg: string, severity?: "error" | "info" | "success" | "warning") => void;
}

const formatCurrency = (value: number) =>
  companyMoney(Number(value || 0));

const DefaultClientsModal: React.FC<DefaultClientsModalProps> = ({
  open,
  onClose,
  routeId,
  routeName,
  date,
  showAlert,
}) => {
  const t = useT();
  const [items, setItems] = useState<DefaultClientItem[]>([]);
  const [loading, setLoading] = useState(false);
  const reqIdRef = useRef(0);

  useEffect(() => {
    if (!open || !routeId || !date) {
      return;
    }

    let active = true;
    const requestId = ++reqIdRef.current;

    const fetchData = async () => {
      setLoading(true);
      try {
        const { data } = await axios.post("/reports/balances/default-clients", {
          routeId,
          date,
        });

        if (!active || requestId !== reqIdRef.current) {
          return;
        }

        setItems(Array.isArray(data?.data) ? data.data : []);
      } catch (error) {
        if (active) {
          console.error(error);
          showAlert(t("clientReports.defaultClientsLoadError"), "error");
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      active = false;
    };
  }, [open, routeId, date, showAlert, t]);

  const rows = items.map((item, index) => ({
    id: item.id || index,
    loanId: item.loanId,
    clientIdNumber: item.clientIdNumber,
    clientFullName: item.clientFullName,
    loanDate: item.loanDate,
    loanValue: item.loanValue,
    balance: item.balance,
    paymentTotal: item.paymentTotal,
    defaultDays: item.defaultDays,
  }));

  type DefaultRow = (typeof rows)[number];
  const money = (field: "loanValue" | "balance" | "paymentTotal", header: string): DataTableColumn<DefaultRow> => ({
    id: field,
    header,
    align: "right",
    sortable: true,
    value: (r) => Number(r[field] ?? 0),
    render: (r) => formatCurrency(Number(r[field] ?? 0)),
  });
  const columns: DataTableColumn<DefaultRow>[] = [
    { id: "loanId", header: t("clientReports.loan"), value: (r) => r.loanId, sortable: true, hideBelow: "sm" },
    { id: "clientIdNumber", header: t("common.idNumber"), value: (r) => String(r.clientIdNumber), sortable: true },
    { id: "clientFullName", header: t("clientReports.client"), value: (r) => r.clientFullName, sortable: true },
    { id: "loanDate", header: t("clientReports.loanDate"), value: (r) => r.loanDate, sortable: true, hideBelow: "sm" },
    money("loanValue", t("clientReports.loanValue")),
    money("balance", t("clientReports.balance")),
    money("paymentTotal", t("clientReports.totalPayments")),
    { id: "defaultDays", header: t("clientReports.defaultDays"), value: (r) => Number(r.defaultDays ?? 0), align: "right", sortable: true },
  ];

  return (
    <StandardModal
      open={open}
      onClose={onClose}
      title={t("clientReports.defaultClientsTitle", { route: routeName || "", date: date || "" })}
      maxWidth={1000}
      loading={loading}
      actions={
        <>
          <span />
          <Button variant="outlined" onClick={onClose}>
            {t("common.close")}
          </Button>
        </>
      }
    >
      {items.length > 0 ? (
        <ModalTable
          rows={rows}
          columns={columns}
          getRowId={(r) => r.id}
          initialSort={{ columnId: "defaultDays", direction: "desc" }}
          exportFileName={t("clientReports.defaultClientsFileName", { id: routeId ?? "" })}
          ariaLabel={t("clientReports.defaultClients")}
        />
      ) : loading ? null : (
        <EmptyState dense title={t("clientReports.noDefaultClients")} />
      )}
    </StandardModal>
  );
};

export default DefaultClientsModal;
